// The pocket calculator behind LG-37b fetta C, as pure functions.
//
// Antenore, 10/09: «una calcolatrice semplice che posso aprire come popup o a
// espansione con cui posso fare dei calcoli io, poi il risultato viene messo
// nel peso totale». It knows nothing about bars, plates or inventories — which
// is why it lives here and not in `plateCalcMath.ts`, where every other helper
// is about the loaded bar.
//
// ⚠️ LEFT TO RIGHT, no operator precedence, and that is a decision, not an
// omission. The approved mockup draws a TAPE above a display that already
// reads the answer — «20 + 20 + 10 + 10» over «60» — with no `=` key to press
// (his call, 10/09). A display that answers before the expression is finished
// can only mean "the running total so far", so each operator closes the one
// before it: `20 + 10 × 2` is 60 here, not 40. It is the pocket-calculator
// model, which is the one a tape describes; a precedence engine would show a
// number that changes retroactively when you press ×.
//
// ⚠️ It DOES know about kg ⇄ lb, and since CN-26 it says so: the module was
// dependency-free until the `LG-85` cure, and an unannounced dependency is
// how a header starts lying. A calculator whose result goes into a weight
// field cannot be unit-agnostic — the swap key is part of the instrument —
// so units are its business and bars, plates and inventories still are not.
// ⛔ Weighed and not taken (audit 11/09): moving the two unit-aware functions
// into a third file would restore the zero-dep header while `CalcState` went
// on carrying `placedKg`, i.e. it would hide the dependency rather than end
// it — and buy a new file for two functions with one caller.

import { fromCanonicalKg, roundTo, toCanonicalKg } from '@heraion/shared';

export type CalcOp = '+' | '-' | '×' | '÷';

const OPS: ReadonlyArray<CalcOp> = ['+', '-', '×', '÷'];
const isOp = (token: string): token is CalcOp =>
  (OPS as ReadonlyArray<string>).includes(token);

/** How many characters one number may be. Stops a held key from overflowing. */
const MAX_ENTRY = 9;

export type CalcState = {
  /**
   * Committed tokens, alternating number and operator: `['20','+','20','+']`.
   * Always ends on an operator (a number is only committed by pressing one).
   *
   * ⚠️ Kept as tokens and not collapsed into a running accumulator so the tape
   * can still SAY «20 + 20 + 10 + 10». An accumulator would hold 50 and have
   * no way to render where it came from — and the tape is what makes a display
   * with no `=` legible.
   */
  tokens: string[];
  /** Digits being typed now. `''` right after an operator. Uses `.` for the
   *  decimal mark whatever the locale writes; the view swaps it for display. */
  entry: string;
  /**
   * True when `entry` was PLACED there — by the seed from the weight field, or
   * by the kg/lb swap — rather than typed key by key. The next digit then
   * REPLACES it instead of appending.
   *
   * ⚠️ Without this the seed is a trap: open the calculator on a 45 kg row,
   * press «2» to start an unrelated sum, and the display reads 452. It is the
   * pocket-calculator rule — a number you did not type is a result, and typing
   * over a result starts again — and it is why a seeded value still needs to
   * be distinguishable from a typed one AFTER it is on screen.
   */
  fresh: boolean;
  /**
   * The canonical kg the display was PLACED from, while it still is exactly
   * that placed number. `null` the moment the athlete types or operates.
   *
   * ⛔ It exists because a displayed number is not the value it came from, and
   * that is `LG-85`: `fromCanonicalKg(100, 'lb')` reads 220.5, and 220.5 lb
   * genuinely IS 100.017 kg — so re-deriving the kilos from the shown text
   * gives back 100.017, not 100. No rounding rule recovers it; the only cure
   * is to have never thrown it away. The `kg ⇄ lb` swap reads THIS, and falls
   * back to the display only when there is no placed value to remember.
   *
   * ⚠️ It is deliberately narrower than `fresh`: `fresh` survives an operator
   * (seeding on 45 exists so you can press «+» and go), but a canonical kg
   * does not — «45 + 5» in pounds is a different sum, so the placed value is
   * gone as soon as an expression opens.
   */
  placedKg: number | null;
};

export const CALC_EMPTY: CalcState = {
  tokens: [],
  entry: '',
  fresh: false,
  placedKg: null,
};

export function calcPressDigit(state: CalcState, digit: string): CalcState {
  // A placed value is typed OVER, not extended.
  if (state.fresh)
    return { ...state, entry: digit, fresh: false, placedKg: null };
  if (state.entry.length >= MAX_ENTRY) return state;
  // A bare leading zero is replaced, not appended to: "0" then "5" is 5, not 05.
  const entry =
    state.entry === '0' && digit !== '0' ? digit : state.entry + digit;
  return { ...state, entry, placedKg: null };
}

export function calcPressDecimal(state: CalcState): CalcState {
  // Same rule as a digit: a decimal point starts a new number over a placed one.
  if (state.fresh)
    return { ...state, entry: '0.', fresh: false, placedKg: null };
  if (state.entry.includes('.')) return state;
  if (state.entry.length >= MAX_ENTRY) return state;
  return {
    ...state,
    entry: state.entry === '' ? '0.' : `${state.entry}.`,
    placedKg: null,
  };
}

export function calcPressOp(state: CalcState, op: CalcOp): CalcState {
  if (state.entry !== '') {
    // ⭐ An operator KEEPS a placed value: seeding on 45 exists so you can press
    // «+» and go. `fresh` only governs what a digit does to it.
    // ⛔ But it does NOT keep the canonical kg: once an expression opens, the
    // result is a number of this unit's own, not the seeded weight any more.
    return {
      tokens: [...state.tokens, state.entry, op],
      entry: '',
      fresh: false,
      placedKg: null,
    };
  }
  // Nothing typed since the last operator: the athlete is correcting the
  // operator, not starting a term. Swap it rather than stacking a second one.
  if (state.tokens.length > 0) {
    return {
      tokens: [...state.tokens.slice(0, -1), op],
      entry: '',
      fresh: false,
      placedKg: null,
    };
  }
  // Nothing at all to operate on. An expression cannot open with an operator.
  return state;
}

export function calcClear(): CalcState {
  return CALC_EMPTY;
}

function apply(left: number, op: CalcOp, right: number): number | null {
  switch (op) {
    case '+':
      return left + right;
    case '-':
      return left - right;
    case '×':
      return left * right;
    case '÷':
      // ⛔ Not Infinity: an unusable number must not reach the weight field,
      // and `calcValue` returning null is what disables "copy into the weight".
      return right === 0 ? null : left / right;
  }
}

/**
 * The number the display shows: the expression evaluated as far as it goes,
 * INCLUDING the term being typed. `null` when there is nothing to show yet or
 * the expression cannot produce a number (a division by zero).
 */
export function calcValue(state: CalcState): number | null {
  let acc: number | null = null;
  let pending: CalcOp | null = null;

  for (const token of [...state.tokens, state.entry]) {
    if (token === '') continue;
    if (isOp(token)) {
      pending = token;
      continue;
    }
    const n = Number(token);
    if (!Number.isFinite(n)) return null;
    if (acc === null) {
      acc = n;
    } else if (pending !== null) {
      acc = apply(acc, pending, n);
      if (acc === null) return null;
    }
  }

  // Floating point: 0.1 + 0.2 must not reach a weight field as
  // 0.30000000000000004. Three decimals is finer than any plate.
  return acc === null ? null : roundTo(acc, 3);
}

/**
 * The expression as the tape prints it, with `decimal` in place of the `.`
 * this module stores — so a French or Italian athlete reads «1,25», which is
 * what the keypad's own comma key wrote.
 */
export function calcTape(state: CalcState, decimal: string): string {
  const parts = [...state.tokens, state.entry].filter((p) => p !== '');
  return parts.join(' ').split('.').join(decimal);
}

/**
 * Restart the calculator on a single known number: the seed from the weight
 * field on open, and the `kg ⇄ lb` swap. Marked `fresh`, so the first digit
 * types over it — see the field's note.
 *
 * `placedKg` is the canonical weight this number is a VIEW of, when the caller
 * knows it (the seed knows it; the swap carries it across). Pass it and the
 * next swap can go back to the exact kilos instead of re-reading the rounded
 * pounds on screen — which is `LG-85`. Omit it and behaviour is as before.
 */
export function calcFromNumber(
  value: number,
  placedKg: number | null = null,
): CalcState {
  return {
    tokens: [],
    entry: String(roundTo(value, 3)),
    fresh: true,
    placedKg,
  };
}

/**
 * The canonical kilos this calculator is currently holding — what «copy into
 * the weight» must write, and what a unit swap must convert FROM.
 *
 * Prefers the placed value over the display, and that is the whole of `LG-85`:
 * a display of 220.5 lb read back gives 100.017 kg, while the weight it was
 * placed from was 100. When nothing was placed — the athlete typed it, or
 * computed it — the display is the only truth, and reading it back is right.
 */
export function calcCanonicalKg(
  state: CalcState,
  unit: 'kg' | 'lb',
): number | null {
  if (state.placedKg !== null) return state.placedKg;
  const value = calcValue(state);
  return value === null ? null : toCanonicalKg(value, unit);
}

/**
 * The `kg ⇄ lb` swap: «il bottone kg/lb per convertire» — it converts the
 * number in the display, on the spot.
 *
 * ⛔ The expression cannot come with it: «20 + 20» is a sum of kilos, and the
 * same two terms in pounds are a different sum. So the tape collapses to the
 * converted result, which is the only honest carry-over — and the canonical
 * kilos ride along, so swapping back lands on the weight you started from.
 */
export function calcSwapUnit(
  state: CalcState,
  from: 'kg' | 'lb',
  to: 'kg' | 'lb',
): CalcState {
  if (from === to) return state;
  const kg = calcCanonicalKg(state, from);
  if (kg === null) return state;
  return calcFromNumber(fromCanonicalKg(kg, to), kg);
}
